<template>
  <g
    class="label-annotation"
    :class="{ selected, editing, dragging }"
  >
    <!-- Fond de l'étiquette (draggable si sélectionné) -->
    <rect
      :x="annotation.data.x - padding"
      :y="annotation.data.y - (fontSize + padding * 2) / 2"
      :width="textWidth + padding * 2 + (selected && !editing ? buttonWidth : 0)"
      :height="fontSize + padding * 2"
      :fill="selected ? 'rgba(74, 158, 255, 0.2)' : 'var(--cv-label-bg)'"
      :stroke="selected ? 'var(--cv-accent)' : 'var(--cv-label-stroke)'"
      :stroke-width="selected ? 8 : 4"
      rx="8"
      class="label-bg"
      :class="{ draggable: selected && !editing }"
      @click.stop="handleClick"
      @mousedown.stop="selected && !editing ? handleDragStart($event) : null"
    />
    
    <!-- Texte de l'étiquette -->
    <text
      v-if="!editing"
      ref="textRef"
      :x="annotation.data.x"
      :y="annotation.data.y"
      :font-size="fontSize"
      :fill="annotation.color || 'var(--cv-annotation-label-default)'"
      class="label-text"
      text-anchor="start"
      dominant-baseline="middle"
      @click.stop="handleClick"
    >
      {{ displayText }}
    </text>

    <!-- Input pour édition -->
    <foreignObject
      v-if="editing"
      :x="annotation.data.x - padding"
      :y="annotation.data.y - (fontSize + padding * 2) / 2"
      :width="Math.max(textWidth + padding * 2, 100)"
      :height="fontSize + padding * 2"
    >
      <input
        ref="inputRef"
        v-model="editText"
        @blur="handleBlur"
        @keydown.enter="handleBlur"
        @keydown.esc="handleCancel"
        @click.stop
        class="label-input"
        :style="{ fontSize: fontSize + 'px' }"
      />
    </foreignObject>

    <!-- Bouton éditer (visible si sélectionné et pas en édition) -->
    <g v-if="selected && !editing" class="edit-button">
      <rect
        :x="annotation.data.x + textWidth + padding"
        :y="annotation.data.y - (fontSize + padding * 2) / 2"
        :width="buttonWidth"
        :height="fontSize + padding * 2"
        fill="var(--cv-accent)"
        rx="8"
        class="button-bg"
        @click.stop="startEditing"
        @mousedown.stop
      />
      <text
        :x="annotation.data.x + textWidth + padding + buttonWidth / 2"
        :y="annotation.data.y"
        :font-size="fontSize"
        fill="white"
        text-anchor="middle"
        dominant-baseline="middle"
        class="button-icon"
        @click.stop="startEditing"
        @mousedown.stop
      >
        ✎
      </text>
    </g>
  </g>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import type { Annotation } from '@api/annotations.api';

const props = defineProps<{
  annotation: Annotation;
  selected: boolean;
  svgRef?: SVGSVGElement | null;
}>();

const emit = defineEmits<{
  select: [annotation: Annotation];
  update: [annotation: Annotation, data: any, save?: boolean];
  delete: [annotation: Annotation];
  move: [annotation: Annotation, x: number, y: number];
}>();

const fontSize = 120;
const padding = 60;
const buttonWidth = 240;
const maxWidth = 2000; // Largeur maximale de l'étiquette en pixels
const editing = ref(false);
const editText = ref('');
const inputRef = ref<HTMLInputElement | null>(null);
const dragging = ref(false);
const dragStartPos = ref({ x: 0, y: 0 });
const dragStartAnnotation = ref({ x: 0, y: 0 });
const lastDraggedData = ref<any | null>(null);
const textRef = ref<SVGTextElement | null>(null);
const actualTextWidth = ref(0);

// Calculer la largeur réelle du texte
const updateTextWidth = () => {
  if (textRef.value) {
    const bbox = textRef.value.getBBox();
    actualTextWidth.value = bbox.width;
  }
};

const displayText = computed(() => {
  const text = props.annotation.data.text || 'Étiquette';
  // Estimation grossière pour la troncature initiale
  const estimatedCharWidth = fontSize * 0.6;
  const maxChars = Math.floor(maxWidth / estimatedCharWidth);
  
  if (text.length > maxChars) {
    return text.substring(0, maxChars - 3) + '...';
  }
  return text;
});

const textWidth = computed(() => {
  // Utiliser la largeur réelle mesurée ou une estimation
  return actualTextWidth.value > 0 ? actualTextWidth.value : displayText.value.length * (fontSize * 0.6);
});

const handleClick = () => {
  if (!props.selected) {
    emit('select', props.annotation);
  }
};

const startEditing = () => {
  editing.value = true;
  editText.value = props.annotation.data.text || '';
  nextTick(() => {
    inputRef.value?.focus();
    inputRef.value?.select();
  });
};

const handleBlur = () => {
  if (editing.value) {
    emit('update', props.annotation, {
      ...props.annotation.data,
      text: editText.value || 'Étiquette',
    });
    editing.value = false;
  }
};

const handleCancel = () => {
  editing.value = false;
  editText.value = props.annotation.data.text || '';
};

const handleDragStart = (event: MouseEvent) => {
  if (!props.selected || editing.value || !props.svgRef) {
    console.log('[LabelAnnotation] Drag impossible:', { selected: props.selected, editing: editing.value, svgRef: !!props.svgRef });
    return;
  }
  
  console.log('[LabelAnnotation] Début drag');
  dragging.value = true;
  lastDraggedData.value = null;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  dragStartPos.value = { x: svgP.x, y: svgP.y };
  dragStartAnnotation.value = { x: props.annotation.data.x, y: props.annotation.data.y };
  
  event.preventDefault();
  event.stopPropagation();
};

const handleDragMove = (event: MouseEvent) => {
  if (!dragging.value || !props.svgRef) return;
  
  const svg = props.svgRef;
  const pt = svg.createSVGPoint();
  pt.x = event.clientX;
  pt.y = event.clientY;
  const svgP = pt.matrixTransform(svg.getScreenCTM()?.inverse());
  
  const deltaX = svgP.x - dragStartPos.value.x;
  const deltaY = svgP.y - dragStartPos.value.y;
  
  const newX = dragStartAnnotation.value.x + deltaX;
  const newY = dragStartAnnotation.value.y + deltaY;
  
  console.log('[LabelAnnotation] Drag move:', { newX, newY });
  
  // Émettre un événement temporaire pour mettre à jour la position visuellement
  const newData = {
    ...props.annotation.data,
    x: newX,
    y: newY,
  };
  lastDraggedData.value = newData;
  emit('update', props.annotation, newData, false);
};

const handleDragEnd = () => {
  if (dragging.value) {
    console.log('[LabelAnnotation] Fin drag');
    dragging.value = false;
    const finalData = lastDraggedData.value;
    lastDraggedData.value = null;
    if (finalData) {
      emit('update', props.annotation, finalData, true);
    }
  }
};

const handleStartEditEvent = (event: Event) => {
  const customEvent = event as CustomEvent;
  if (customEvent.detail.annotationId === props.annotation.id) {
    startEditing();
  }
};

onMounted(() => {
  window.addEventListener('mousemove', handleDragMove);
  window.addEventListener('mouseup', handleDragEnd);
  window.addEventListener('start-annotation-edit', handleStartEditEvent as EventListener);
});

onUnmounted(() => {
  window.removeEventListener('mousemove', handleDragMove);
  window.removeEventListener('mouseup', handleDragEnd);
  window.removeEventListener('start-annotation-edit', handleStartEditEvent as EventListener);
});

// Ouvrir l'édition automatiquement pour les nouvelles étiquettes
watch(() => props.selected, (isSelected) => {
  if (isSelected && !props.annotation.data.text) {
    nextTick(() => startEditing());
  }
});

// Mettre à jour la largeur du texte quand il change
watch(() => props.annotation.data.text, () => {
  nextTick(() => updateTextWidth());
}, { immediate: true });
</script>

<style scoped>
.label-annotation {
  cursor: pointer;
}

.label-annotation.dragging {
  opacity: 0.8;
}

.label-bg.draggable {
  cursor: move;
}

.label-bg.draggable:hover {
  fill: rgba(74, 158, 255, 0.3);
}

.label-text {
  pointer-events: none;
  user-select: none;
}

.label-input {
  width: 100%;
  height: 100%;
  background: var(--cv-label-input-bg);
  border: 1px solid var(--cv-accent);
  border-radius: 4px;
  color: var(--cv-label-input-color);
  padding: 4px 6px;
  outline: none;
  font-family: inherit;
}

.edit-button {
  cursor: pointer;
}

.button-bg:hover {
  opacity: 0.92;
}

.button-icon {
  pointer-events: none;
  user-select: none;
}
</style>
